import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { DocumentTypeFilter, DocumentTypeService } from '../../../../services/document-type.service';
import { IDocumentType, IStandardDocumentType } from '../../../../models/settings.model';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { FilterPipe } from '../../../../../pipes/filter.pipe';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { DocumentTypesListComponent } from '../../document-types-list/document-types-list.component';
import { IStatus, STATUS_TYPE } from '../../../../../@core/models/model';
import { StatusService } from '../../../../services/status.service';
import { ToastService } from '../../../../../@core/services/toast.service';
import { DialogComponent } from '../../../../../@core/components/ui/dialog/dialog.component';
import { KNOWLEDGE_BASE_SVG } from '../../knowledgebase/knowledge-base-svg';
import { SanitizeHtmlPipe } from "../../../../../@shared/pipes/sanitizeHtml.pipe";
import { STANDARD_DOCUMENT_STATUS, standardDocumentStatusMappings } from './document-status.enum';
import { STANDARD_DOCUMENT_TYPES } from '../../../../../@core/models/enums';
import { ButtonComponent } from "../../../../../@core/components/ui/button/button.component";
import { DeleteConfirmationComponent } from "../../../../../@core/components/ui/delete-confirmation/delete-confirmation.component";



@Component({
    selector: 'app-document-status',
    imports: [
        CommonModule,
        FormsModule,
        DatePickerModule,
        CheckboxModule,
        ButtonModule,
        InputGroupModule,
        InputGroupAddonModule,
        SkeletonModule,
        TooltipModule,
        MenuModule,
        DocumentTypesListComponent,
        SanitizeHtmlPipe,
        DialogComponent,
        ButtonComponent,
        DeleteConfirmationComponent
    ],
    standalone: true,
    templateUrl: './document-status.component.html',
    styleUrl: './document-status.component.scss'
})
export class DocumentStatusComponent implements OnInit {

    selectedDocType!: IDocumentType;
    addEditStatusVisible: boolean = false;
    edit: boolean = false;
    newStatusName: string = ''
    statusLoading: boolean = false
    statusOptions: IStatus[] = []
    selectedStatus: IStatus | null = null;
    docTypeLoading: boolean = false;
    docTypes: IDocumentType[] = [];
    SVGS = KNOWLEDGE_BASE_SVG;
    deleteStatusDialog: boolean = false;

    constructor(
        private statusService: StatusService,
        private toastService: ToastService,
        private documentTypeService: DocumentTypeService
    ) { }

    ngOnInit() {
        this.getDocumentTypes();
    }

    getDocumentTypes() {
        this.docTypeLoading = true;
        this.documentTypeService.getDocTypes([DocumentTypeFilter.GENERAL, DocumentTypeFilter.FORM_RELATED]).subscribe(res => {
            if (res && res.success) {
                this.docTypeLoading = false;
                this.docTypes = res.data;
            } else {
                this.docTypeLoading = false;
            }
        });
    }

    onDocumentTypeSelected(docType: IDocumentType) {
        this.selectedDocType = docType
        this.getStatusesForDocType();
    }

    getStatusesForDocType() {
        this.statusService.getStatuses(STATUS_TYPE.DOCUMENT, this.selectedDocType._id).subscribe(res => {
            if (res && res.success) {
                this.statusLoading = false
                this.statusOptions = res.data
            }
        })
    }

    addStatus() {
        this.edit = false;
        this.newStatusName = '';
        this.addEditStatusVisible = true;
    }

    editStatus(status: IStatus): void {
        this.edit = true;
        this.selectedStatus = status;
        this.newStatusName = status.name
        this.addEditStatusVisible = true;
    }


    deleteSelectedStatus() {
        this.statusService.deleteStatus(this.selectedStatus?._id || '').subscribe(res => {
            if (res && res.success) {
                this.toastService.success("Status deleted successfully.")
                this.getStatusesForDocType()
                this.deleteStatusDialog = false;
            }
        })
    }


    openDeleteDialog(status: IStatus) {
        this.selectedStatus = status
        this.deleteStatusDialog = true
    }

    saveNewStatus(): void {
        if (this.newStatusName && this.newStatusName.trim()) {
            if (this.edit && this.selectedStatus) {
                this.updateDocumentTypeStatus()
            } else {
                this.createDocumentTypeStatus()
            }
        }
    }

    createDocumentTypeStatus() {
        this.statusService.addStatus(this.newStatusName, STATUS_TYPE.DOCUMENT, this.selectedDocType._id).subscribe(res => {
            if (res && res.success) {
                this.toastService.success('Document type status successfully created.')
                this.addEditStatusVisible = false;
                this.newStatusName = '';
                this.getStatusesForDocType()
            }
        })
    }

    updateDocumentTypeStatus() {
        this.statusService.updateStatus(this.newStatusName, this.selectedStatus?._id || '', STATUS_TYPE.DOCUMENT, this.selectedDocType._id).subscribe(res => {
            if (res && res.success) {
                this.toastService.success('Document type status successfully updated.')
                this.addEditStatusVisible = false;
                this.edit = false;
                this.newStatusName = '';
                this.getStatusesForDocType();
            }
        });
    }

    cancelAddStatus(): void {
        this.addEditStatusVisible = false;
        this.newStatusName = '';
        this.edit = false;
        this.selectedStatus = null;
    }

    canDeleteStatus(status: IStatus): boolean {
        if (!status.standardStatus) {
            return true;
        }
        const standardDocType = this.selectedDocType.name.toUpperCase() as STANDARD_DOCUMENT_TYPES;
        const statusMappings = standardDocumentStatusMappings[standardDocType] || standardDocumentStatusMappings['GENERIC'];
        if (statusMappings.length === 0) {
            return true;
        }
        const firstStatus = statusMappings[0];
        const lastStatus = statusMappings[statusMappings.length - 1];
        return status.name !== firstStatus && status.name !== lastStatus;
    }

    get hasChanges(): boolean {
        if (!this.edit) return this.newStatusName.length > 0;
        return this.newStatusName !== this.selectedStatus?.name;
    }
}
